import React, { useEffect, useState } from "react";
import AppShell from "../../components/layout/AppShell";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import {
  Users, Plus, PencilSimple, Prohibit, ArrowCounterClockwise,
  MagnifyingGlass, X, Gear, Phone, MapPin,
} from "@phosphor-icons/react";

const RADII = [5, 10, 20, 50, 100];
const STATUS_COLOR = {
  active: "bg-[#C7E0C2] text-[#2D5A27]",
  suspended: "bg-[#F4C7B9] text-[#7A2E18]",
  pending: "bg-[#F9E4B7] text-[#7A5A11]",
};

const empty = {
  name: "", phone: "", email: "",
  aadhaar_no: "", pan_no: "",
  bank_account: "", ifsc: "", upi_id: "",
  vehicle_type: "bike", vehicle_number: "",
  state_id: "", district_id: "",
  assigned_lsgds: "",
  assigned_drop_locations: [],   // array of ids (multi-select)
  service_radius_km: 10,
  service_categories: "",
  notes: "",
};

export default function GigPartnersAdmin() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("all");  // all | active | suspended | pending
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null);     // null = create
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settings, setSettings] = useState(null);
  const [dropLocations, setDropLocations] = useState([]);
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [scope, setScope] = useState({ limited: false });

  const load = async () => {
    try {
      const r = await api.get("/gig-partners", {
        params: { status: tab === "all" ? undefined : tab, q: q || undefined },
      });
      setRows(r.data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab, q]);
  useEffect(() => {
    api.get("/gig-partners/settings").then(r => setSettings(r.data))
       .catch(() => {});
    api.get("/gig-partners/drop-locations").then(r => setDropLocations(r.data || []))
       .catch(() => {});
    api.get("/gig-partners/scope").then(r => setScope(r.data || { limited: false }))
       .catch(() => {});
    // Load states + districts for the hierarchy pickers.
    api.get("/master/locations").then(r => {
      const locs = r.data || [];
      setStates(locs.filter(l => l.type === "state"));
      setDistricts(locs.filter(l => l.type === "district"));
    }).catch(() => {});
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(empty);
    setEditOpen(true);
  };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      ...empty, ...p,
      assigned_lsgds: (p.assigned_lsgds || []).join(", "),
      assigned_drop_locations: p.assigned_drop_locations || [],
      service_categories: (p.service_categories || []).join(", "),
      state_id: p.state_id || "",
      district_id: p.district_id || "",
    });
    setEditOpen(true);
  };

  const save = async () => {
    setBusy(true);
    const payload = {
      ...form,
      state_id: form.state_id || null,
      district_id: form.district_id || null,
      service_radius_km: parseInt(form.service_radius_km, 10) || 10,
      assigned_lsgds: form.assigned_lsgds.split(",").map(s => s.trim()).filter(Boolean),
      assigned_drop_locations: form.assigned_drop_locations || [],
      service_categories: form.service_categories.split(",").map(s => s.trim()).filter(Boolean),
    };
    try {
      if (editing) {
        await api.patch(`/gig-partners/${editing.id}`, payload);
        toast.success("Partner updated");
      } else {
        await api.post("/gig-partners", payload);
        toast.success("Partner added");
      }
      setEditOpen(false);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setBusy(false);
  };

  const suspend = async (p) => {
    const reason = window.prompt(`Reason for suspending ${p.name}?`);
    if (!reason) return;
    try {
      await api.post(`/gig-partners/${p.id}/suspend`, { reason });
      toast.success("Suspended");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const reinstate = async (p) => {
    try {
      await api.post(`/gig-partners/${p.id}/reinstate`);
      toast.success("Reinstated");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const Row = ({ p }) => (
    <tr className="border-b border-[#E5E2D9] hover:bg-[#F7F6F2]"
         data-testid={`gig-row-${p.id}`}>
      <td className="px-4 py-3">
        <div className="font-medium text-sm">{p.name}</div>
        <div className="text-[11px] text-[#8A8780] flex items-center gap-1 mt-0.5">
          <Phone size={11}/> {p.phone}
        </div>
      </td>
      <td className="px-4 py-3 text-xs">
        <span className={`px-2 py-0.5 rounded ${STATUS_COLOR[p.status] || "bg-[#F0EEE4]"}`}>
          {p.status}
        </span>
        <span className="ml-2 text-[11px] text-[#8A8780]">
          {p.duty_status === "on" ? "ON DUTY" : "off"}
        </span>
      </td>
      <td className="px-4 py-3 text-xs">
        {p.vehicle_type || "—"} · {p.vehicle_number || ""}
      </td>
      <td className="px-4 py-3 text-xs">
        {p.service_radius_km} km
      </td>
      <td className="px-4 py-3 text-xs">
        {p.rating_avg != null ? `${p.rating_avg.toFixed(2)} ★` : "—"}
        <span className="text-[11px] text-[#8A8780] ml-1">
          · {p.orders_lifetime || 0} orders
        </span>
      </td>
      <td className="px-4 py-3 text-right">
        <div className="flex gap-1 justify-end">
          <button onClick={() => openEdit(p)} data-testid={`gig-edit-${p.id}`}
                   className="h-7 w-7 rounded hover:bg-[#F0EEE4] flex items-center justify-center">
            <PencilSimple size={14}/>
          </button>
          {p.status === "active" ? (
            <button onClick={() => suspend(p)} data-testid={`gig-suspend-${p.id}`}
                     className="h-7 w-7 rounded hover:bg-[#F4C7B9] text-[#7A2E18] flex items-center justify-center">
              <Prohibit size={14}/>
            </button>
          ) : (
            <button onClick={() => reinstate(p)} data-testid={`gig-reinstate-${p.id}`}
                     className="h-7 w-7 rounded hover:bg-[#C7E0C2] text-[#2D5A27] flex items-center justify-center">
              <ArrowCounterClockwise size={14}/>
            </button>
          )}
        </div>
      </td>
    </tr>
  );

  return (
    <AppShell title="Gig Partners · Marketplace">
      <div data-testid="gig-partners-admin" className="space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#8A8780]">Workforce</div>
            <h1 className="text-2xl font-heading flex items-center gap-2 mt-1">
              <Users size={22} weight="duotone"/>
              Gig Partners
            </h1>
            <p className="text-sm text-[#5C5A55] mt-1">
              Part-time field staff who can accept nearby prepaid service requests.
            </p>
            {scope.limited && (
              <div className="mt-2 inline-flex items-center gap-2 px-2 py-1 rounded-md bg-[#F9E4B7] text-[#7A5A11] text-[11px]"
                    data-testid="gig-scope-banner">
                Your region: {(scope.states || []).map(s => s.name).join(", ") || "—"}
                {(scope.districts || []).length > 0 && (
                  <> · {(scope.districts || []).map(d => d.name).join(", ")}</>
                )}
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <button onClick={() => (window.location.href = "/admin/onboarding-managers")}
                     data-testid="gig-open-onboarding"
                     className="h-9 px-3 rounded-md border border-[#D9D5C6] bg-white text-xs flex items-center gap-2">
              Onboarding Managers
            </button>
            <button onClick={() => setSettingsOpen(true)} data-testid="gig-open-settings"
                     className="h-9 px-3 rounded-md border border-[#D9D5C6] bg-white text-xs flex items-center gap-2">
              <Gear size={14}/> Dispatch settings
            </button>
            <button onClick={openCreate} data-testid="gig-new"
                     className="h-9 px-3 rounded-md bg-[#2D5A27] text-white text-xs flex items-center gap-2 hover:bg-[#3A7033]">
              <Plus size={14}/> New partner
            </button>
          </div>
        </div>

        {/* Tabs + search */}
        <div className="flex items-center gap-2 flex-wrap">
          {["all", "active", "suspended", "pending"].map(k => (
            <button key={k} onClick={() => setTab(k)}
                     data-testid={`gig-tab-${k}`}
                     className={`h-8 px-3 rounded-full text-xs border transition ${
                       tab === k ? "bg-[#2D5A27] text-white border-[#2D5A27]"
                                 : "bg-white border-[#D9D5C6]"}`}>
              {k}
            </button>
          ))}
          <div className="ml-auto flex items-center gap-2 bg-white rounded-md border border-[#D9D5C6] px-2 h-8">
            <MagnifyingGlass size={14}/>
            <input value={q} onChange={e => setQ(e.target.value)}
                    placeholder="name · phone · email" data-testid="gig-search"
                    className="h-full text-xs outline-none bg-transparent w-48"/>
            {q && <button onClick={() => setQ("")}><X size={14}/></button>}
          </div>
        </div>

        <div className="bg-white rounded-lg border border-[#E5E2D9] overflow-hidden">
          <table className="w-full text-left">
            <thead className="bg-[#F7F6F2] text-[11px] uppercase tracking-wider text-[#8A8780]">
              <tr>
                <th className="px-4 py-2">Partner</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2">Vehicle</th>
                <th className="px-4 py-2">Radius</th>
                <th className="px-4 py-2">Rating</th>
                <th className="px-4 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan="6" className="p-8 text-center text-sm text-[#8A8780]">
                  No partners yet. Add one to open your marketplace.
                </td></tr>
              ) : rows.map(p => <Row key={p.id} p={p}/>)}
            </tbody>
          </table>
        </div>

        {/* ---------- Create / Edit drawer ---------- */}
        {editOpen && (
          <div className="fixed inset-0 z-40 bg-black/40 flex justify-end" data-testid="gig-drawer">
            <div className="w-full max-w-md bg-white h-full overflow-y-auto">
              <div className="p-5 border-b border-[#E5E2D9] flex items-center justify-between">
                <h3 className="text-xl font-heading">{editing ? "Edit partner" : "New partner"}</h3>
                <button onClick={() => setEditOpen(false)} data-testid="gig-drawer-close">
                  <X size={18}/>
                </button>
              </div>
              <div className="p-5 space-y-3 text-sm">
                {["name", "phone", "email", "aadhaar_no", "pan_no",
                  "bank_account", "ifsc", "upi_id",
                  "vehicle_number"].map(k => (
                  <div key={k}>
                    <label className="block text-[11px] uppercase tracking-wider text-[#8A8780]">
                      {k.replace(/_/g, " ")}
                    </label>
                    <input value={form[k] || ""}
                            onChange={e => setForm(f => ({...f, [k]: e.target.value}))}
                            data-testid={`gig-input-${k}`}
                            className="h-9 w-full px-2 border border-[#D9D5C6] rounded-md"/>
                  </div>
                ))}
                <div>
                  <label className="block text-[11px] uppercase tracking-wider text-[#8A8780]">
                    Vehicle type
                  </label>
                  <select value={form.vehicle_type || "bike"}
                           onChange={e => setForm(f => ({...f, vehicle_type: e.target.value}))}
                           data-testid="gig-vehicle-type"
                           className="h-9 w-full px-2 border border-[#D9D5C6] rounded-md bg-white">
                    {["bike", "scooter", "auto", "van", "car", "none"].map(v =>
                      <option key={v} value={v}>{v}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] uppercase tracking-wider text-[#8A8780]">
                    Service radius (km)
                  </label>
                  <div className="flex gap-2 mt-1">
                    {RADII.map(r => (
                      <button key={r} type="button" onClick={() => setForm(f => ({...f, service_radius_km: r}))}
                               data-testid={`gig-radius-${r}`}
                               className={`h-8 px-3 rounded-full text-xs border ${
                                 Number(form.service_radius_km) === r
                                   ? "bg-[#2D5A27] text-white border-[#2D5A27]"
                                   : "border-[#D9D5C6] bg-white"}`}>
                        {r}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-[11px] uppercase tracking-wider text-[#8A8780]">
                    Assigned LSGDs (comma-separated)
                  </label>
                  <input value={form.assigned_lsgds}
                          onChange={e => setForm(f => ({...f, assigned_lsgds: e.target.value}))}
                          data-testid="gig-lsgds"
                          className="h-9 w-full px-2 border border-[#D9D5C6] rounded-md"/>
                </div>
                <div>
                  <label className="block text-[11px] uppercase tracking-wider text-[#8A8780]">
                    State
                  </label>
                  <select value={form.state_id || ""}
                           onChange={e => setForm(f => ({...f, state_id: e.target.value,
                                                            district_id: ""}))}
                           data-testid="gig-state"
                           className="h-9 w-full px-2 border border-[#D9D5C6] rounded-md bg-white">
                    <option value="">— Select state —</option>
                    {states
                      .filter(s => !scope.limited || (scope.state_ids || []).includes(s.id)
                                     || districts.some(d => d.parent_id === s.id && (scope.district_ids || []).includes(d.id)))
                      .map(s => (
                        <option key={s.id} value={s.id}>{s.name}</option>
                      ))}
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] uppercase tracking-wider text-[#8A8780]">
                    District
                  </label>
                  <select value={form.district_id || ""}
                           onChange={e => setForm(f => ({...f, district_id: e.target.value}))}
                           data-testid="gig-district"
                           className="h-9 w-full px-2 border border-[#D9D5C6] rounded-md bg-white">
                    <option value="">— Select district —</option>
                    {districts
                      .filter(d => (!form.state_id || d.parent_id === form.state_id))
                      .filter(d => !scope.limited
                                     || (scope.district_ids || []).includes(d.id)
                                     || (scope.state_ids || []).includes(d.parent_id))
                      .map(d => (
                        <option key={d.id} value={d.id}>{d.name}</option>
                      ))}
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] uppercase tracking-wider text-[#8A8780]">
                    Drop locations (multi-select from master)
                  </label>
                  <div className="border border-[#D9D5C6] rounded-md p-2 max-h-48 overflow-y-auto space-y-1"
                        data-testid="gig-drops">
                    {dropLocations.length === 0 && (
                      <div className="text-xs text-[#8A8780] p-2">
                        No drop locations yet — create one in Master → Drop Locations.
                      </div>
                    )}
                    {dropLocations
                      .filter(d => !form.district_id || d.district_id === form.district_id || !d.district_id)
                      .map(d => {
                        const checked = (form.assigned_drop_locations || []).includes(d.id);
                        return (
                          <label key={d.id}
                                   data-testid={`gig-drop-${d.id}`}
                                   className={`flex items-center gap-2 px-2 py-1 rounded cursor-pointer text-sm hover:bg-[#F7F6F2] ${
                                     checked ? "bg-[#F0EEE4]" : ""}`}>
                            <input type="checkbox" checked={checked}
                                    onChange={e => setForm(f => {
                                      const cur = new Set(f.assigned_drop_locations || []);
                                      if (e.target.checked) cur.add(d.id); else cur.delete(d.id);
                                      return {...f, assigned_drop_locations: Array.from(cur)};
                                    })}/>
                            <span className="flex-1">{d.name}</span>
                            {d.access === "private" &&
                              <span className="text-[10px] text-[#8A8780]">private</span>}
                          </label>
                        );
                      })}
                  </div>
                </div>
                <div>
                  <label className="block text-[11px] uppercase tracking-wider text-[#8A8780]">
                    Service categories (comma-separated)
                  </label>
                  <input value={form.service_categories}
                          onChange={e => setForm(f => ({...f, service_categories: e.target.value}))}
                          data-testid="gig-cats"
                          className="h-9 w-full px-2 border border-[#D9D5C6] rounded-md"/>
                </div>
                <div>
                  <label className="block text-[11px] uppercase tracking-wider text-[#8A8780]">
                    Notes
                  </label>
                  <textarea value={form.notes || ""}
                             onChange={e => setForm(f => ({...f, notes: e.target.value}))}
                             data-testid="gig-notes"
                             className="w-full p-2 border border-[#D9D5C6] rounded-md min-h-[80px]"/>
                </div>
                <button onClick={save} disabled={busy || !form.name || !form.phone}
                         data-testid="gig-save"
                         className="h-11 w-full rounded-md bg-[#2D5A27] text-white text-sm font-medium disabled:opacity-50">
                  {busy ? "Saving…" : editing ? "Save changes" : "Create partner"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ---------- Settings drawer ---------- */}
        {settingsOpen && settings && (
          <SettingsDrawer settings={settings} onClose={() => setSettingsOpen(false)}
                            onSave={(s) => setSettings(s)}/>
        )}
      </div>
    </AppShell>
  );
}

function SettingsDrawer({ settings, onClose, onSave }) {
  const [s, setS] = useState(settings);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const payload = {};
      ["dispatch_mode", "default_allow_partners", "offer_ttl_s",
       "base_fee", "free_km", "per_km_rate", "min_charge",
       "grace_window_s", "acceptance_fee", "cancellation_fee", "no_show_fee",
       "rating_floor"].forEach(k => { payload[k] = s[k]; });
      const r = await api.patch("/gig-partners/settings", payload);
      onSave(r.data);
      toast.success("Settings saved");
      onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  const Num = ({ k, label, step = 1 }) => (
    <div>
      <label className="block text-[11px] uppercase tracking-wider text-[#8A8780]">{label}</label>
      <input type="number" step={step} value={s[k] ?? 0}
              onChange={e => setS(prev => ({...prev, [k]: Number(e.target.value)}))}
              data-testid={`gig-setting-${k}`}
              className="h-9 w-full px-2 border border-[#D9D5C6] rounded-md"/>
    </div>
  );
  return (
    <div className="fixed inset-0 z-40 bg-black/40 flex justify-end" data-testid="gig-settings-drawer">
      <div className="w-full max-w-md bg-white h-full overflow-y-auto">
        <div className="p-5 border-b border-[#E5E2D9] flex items-center justify-between">
          <h3 className="text-xl font-heading">Dispatch settings</h3>
          <button onClick={onClose}><X size={18}/></button>
        </div>
        <div className="p-5 space-y-3 text-sm">
          <div>
            <label className="block text-[11px] uppercase tracking-wider text-[#8A8780]">Dispatch mode</label>
            <select value={s.dispatch_mode}
                     onChange={e => setS(prev => ({...prev, dispatch_mode: e.target.value}))}
                     data-testid="gig-setting-dispatch_mode"
                     className="h-9 w-full px-2 border border-[#D9D5C6] rounded-md bg-white">
              <option value="broadcast">Broadcast · everyone within 5 km</option>
              <option value="nearest_ring">Nearest ring · closest first</option>
              <option value="round_robin">Fairness · fewest orders today</option>
            </select>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={!!s.default_allow_partners}
                    onChange={e => setS(prev => ({...prev, default_allow_partners: e.target.checked}))}
                    data-testid="gig-setting-default_allow_partners"/>
            Default "allow partners" ON for new SRs
          </label>
          <div className="grid grid-cols-2 gap-3">
            <Num k="offer_ttl_s" label="Offer timeout (s)"/>
            <Num k="grace_window_s" label="Cancel grace (s)"/>
            <Num k="base_fee" label="Base fee (₹)"/>
            <Num k="free_km" label="Free km" step={0.1}/>
            <Num k="per_km_rate" label="Per-km rate (₹)"/>
            <Num k="min_charge" label="Min charge (₹)"/>
            <Num k="acceptance_fee" label="Acceptance fee (₹)"/>
            <Num k="cancellation_fee" label="Cancellation fee (₹)"/>
            <Num k="no_show_fee" label="No-show fee (₹)"/>
            <Num k="rating_floor" label="Rating floor" step={0.1}/>
          </div>
          <button onClick={save} disabled={busy} data-testid="gig-settings-save"
                   className="h-11 w-full rounded-md bg-[#2D5A27] text-white text-sm font-medium">
            {busy ? "Saving…" : "Save settings"}
          </button>
        </div>
      </div>
    </div>
  );
}
